import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface SessionTimeoutWarningProps {
  isOpen: boolean;
  remainingSeconds: number;
  onStayLoggedIn: () => void;
  onLogout: () => void;
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  isOpen,
  remainingSeconds,
  onStayLoggedIn,
  onLogout,
}) => {
  // We use a local state to animate the progress smoothly if needed, but for now
  // we can just use the remainingSeconds directly.
  const totalSeconds = 60; // Assuming a 60-second warning window
  const percentage = Math.max(0, Math.min(100, (remainingSeconds / totalSeconds) * 100));

  // Circular progress SVG logic
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onStayLoggedIn(); }}>
      {/* We hide the close button by styling the content to not show the X, 
          or we can just let users close it by clicking outside (which acts as "stay logged in") */}
      <DialogContent className="sm:max-w-md text-center p-8 border-none shadow-2xl rounded-2xl bg-white dark:bg-slate-900">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-2xl font-semibold text-center mt-2 text-slate-800 dark:text-slate-100">
            Your session is expiring in ...
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center my-8">
          {/* Circular Timer */}
          <div className="relative flex items-center justify-center w-36 h-36">
            <svg
              className="transform -rotate-90 w-full h-full"
              viewBox="0 0 100 100"
            >
              {/* Background Circle */}
              <circle
                cx="50"
                cy="50"
                r={radius}
                stroke="currentColor"
                strokeWidth="6"
                fill="transparent"
                className="text-slate-100 dark:text-slate-800"
                strokeDasharray="4 4"
              />
              {/* Progress Circle */}
              <circle
                cx="50"
                cy="50"
                r={radius}
                stroke="currentColor"
                strokeWidth="6"
                fill="transparent"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                className="text-primary transition-all duration-1000 ease-linear"
              />
            </svg>
            <div className="absolute flex flex-col items-center justify-center">
              <span className="text-4xl font-bold text-primary tabular-nums">
                {remainingSeconds}
              </span>
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">
                sec
              </span>
            </div>
          </div>
        </div>

        <p className="text-slate-600 dark:text-slate-400 mb-8 font-medium">
          To stay logged in, click on the button below
        </p>

        <div className="flex justify-center gap-4 w-full">
          <Button
            variant="outline"
            className="flex-1 py-6 text-base font-semibold border-2 hover:bg-slate-50 dark:hover:bg-slate-800"
            onClick={onLogout}
          >
            Logout
          </Button>
          <Button
            className="flex-1 py-6 text-base font-semibold shadow-md"
            onClick={onStayLoggedIn}
          >
            Stay Logged In
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
